import React from 'react';
import { Box, Card, CardContent, Typography, Chip, keyframes } from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  ReportProblem as AlertIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AttendantHighlight } from './types';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(120,105,223,0); }
  50% { box-shadow: 0 0 18px rgba(120,105,223,0.18), 0 0 32px rgba(102,126,234,0.10); }
`;

const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

interface Props {
  topPraised: AttendantHighlight[];
  needAttention: AttendantHighlight[];
  loading: boolean;
}

const SkeletonCard: React.FC = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height: 340,
    }}
  />
);

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const avatarColor = (name: string) => {
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#0ea5e9', '#14b8a6', '#84cc16'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// ─── Panel ────────────────────────────────────────────────────────────────
interface PanelProps {
  title: string;
  items: AttendantHighlight[];
  kind: 'praise' | 'criticism';
}

const Panel: React.FC<PanelProps> = ({ title, items, kind }) => {
  const baseColor = kind === 'praise' ? '#22c55e' : '#ef4444';
  const gradient = kind === 'praise'
    ? 'linear-gradient(135deg, #22c55e, #16a34a)'
    : 'linear-gradient(135deg, #ef4444, #dc2626)';
  const icon = kind === 'praise'
    ? <TrophyIcon sx={{ color: '#fff', fontSize: 14 }} />
    : <AlertIcon sx={{ color: '#fff', fontSize: 14 }} />;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 28, height: 28, borderRadius: 1.5,
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 3px 10px ${baseColor}30`,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
          {title}
        </Typography>
      </Box>

      {items.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
          {kind === 'praise' ? 'Nenhum destaque no período' : 'Nenhum frentista em alerta'}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item, idx) => {
            const rate = kind === 'praise' ? (item.praiseRate ?? 0) : (item.criticismRate ?? 0);
            const bgC = avatarColor(item.name);
            return (
              <motion.div
                key={item.attendantId}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05, duration: 0.25, ease: EASE_OUT }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    border: `1px solid ${baseColor}20`,
                    bgcolor: `${baseColor}06`,
                    transition: 'background 0.15s, border-color 0.15s',
                    '&:hover': { bgcolor: `${baseColor}10`, borderColor: `${baseColor}40` },
                  }}
                >
                  {/* Rank */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: baseColor,
                      width: 16,
                      textAlign: 'right',
                      flexShrink: 0,
                      fontSize: '0.78rem',
                    }}
                  >
                    {idx + 1}
                  </Typography>

                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 34, height: 34, borderRadius: '50%',
                      bgcolor: bgC,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                      {initials(item.name)}
                    </Typography>
                  </Box>

                  {/* Name + top pollItem */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.3, fontSize: '0.85rem' }}
                      noWrap
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', fontSize: '0.72rem' }}
                      noWrap
                    >
                      {item.topPollItem ? `"${item.topPollItem}"` : `${item.totalRatings} avaliações`}
                    </Typography>
                  </Box>

                  {/* Rate chip */}
                  <Chip
                    label={`${Math.round(rate)}%`}
                    size="small"
                    sx={{
                      bgcolor: '#fff',
                      color: baseColor,
                      fontWeight: 800,
                      fontSize: '0.75rem',
                      height: 22,
                      border: `1px solid ${baseColor}40`,
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </motion.div>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Main ──────────────────────────────────────────────────────────────────
const AttendantHighlights: React.FC<Props> = ({ topPraised, needAttention, loading }) => {
  if (loading) return <SkeletonCard />;

  if (topPraised.length === 0 && needAttention.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE_OUT }}
    >
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid rgba(120,105,223,0.10)',
          animation: `${pulseGlow} 4s ease-in-out infinite`,
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>
              Frentistas — Destaques e Alertas
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 2 }}>
            Baseado nos pollItems selecionados pelos clientes. Mínimo 10 avaliações no período.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1px 1fr' },
              gap: 2,
            }}
          >
            <Panel title="Mais elogiados" items={topPraised} kind="praise" />
            <Box
              sx={{
                width: 1,
                display: { xs: 'none', sm: 'block' },
                background: 'linear-gradient(180deg, transparent 0%, #e2e8f0 20%, #e2e8f0 80%, transparent 100%)',
              }}
            />
            <Panel title="Precisam de atenção" items={needAttention} kind="criticism" />
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AttendantHighlights;
